// Network configurations
export const NETWORKS = {
  MTN: {
    name: 'mtn',
    displayName: 'MTN',
    color: '#FFD700',
    mainColor: '#FFD700',
    gradient: 'from-yellow-400 to-yellow-500',
    darkGradient: 'from-yellow-500 to-yellow-600',
    badgeClass: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200',
    tabActiveClass: 'bg-yellow-500 text-white border border-yellow-500',
    tabInactiveClass: 'bg-yellow-50 text-yellow-900 border border-yellow-200 hover:bg-yellow-100',
  },
  AIRTELTIGO: {
    name: 'airteltigo',
    displayName: 'AirtelTigo',
    color: '#0052CC',
    mainColor: '#0052CC',
    gradient: 'from-blue-500 to-blue-600',
    darkGradient: 'from-blue-600 to-blue-700',
    badgeClass: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
    tabActiveClass: 'bg-blue-600 text-white border border-blue-600',
    tabInactiveClass: 'bg-blue-50 text-blue-900 border border-blue-200 hover:bg-blue-100',
  },
  TELECEL: {
    name: 'telecel',
    displayName: 'Telecel',
    color: '#DC2626',
    mainColor: '#DC2626',
    gradient: 'from-red-500 to-red-600',
    darkGradient: 'from-red-600 to-red-700',
    badgeClass: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
    tabActiveClass: 'bg-red-600 text-white border border-red-600',
    tabInactiveClass: 'bg-red-50 text-red-900 border border-red-200 hover:bg-red-100',
  },
} as const;

export const normalizeNetworkName = (network?: string) => {
  const normalized = String(network || '').toLowerCase().trim();
  if (normalized === 'mtn') return 'mtn';
  if (normalized === 'airteltigo' || normalized === 'airtel tigo' || normalized === 'airtel-tigo') return 'airteltigo';
  if (normalized === 'telecel') return 'telecel';
  return '';
};

const NETWORKS_BY_NAME = {
  mtn: NETWORKS.MTN,
  airteltigo: NETWORKS.AIRTELTIGO,
  telecel: NETWORKS.TELECEL
} as const;

export const getNetworkUiConfig = (network?: string) => {
  const normalized = normalizeNetworkName(network);
  return normalized ? NETWORKS_BY_NAME[normalized] : null;
};

// API endpoints
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    ME: '/auth/me',
    PROFILE: '/auth/profile',
    PASSWORD: '/auth/password',
    SETTINGS: '/auth/settings',
    REFRESH: '/auth/refresh',
  },
  PACKAGES: {
    LIST: '/packages',
    DETAIL: (id: string) => `/packages/${id}`,
    NETWORK: (network: string) => `/packages/network/${network}`,
    POPULAR: '/packages/popular/list',
    SEARCH: '/packages/search/query',
    CREATE: '/packages',
    UPDATE: (id: string) => `/packages/${id}`,
    DELETE: (id: string) => `/packages/${id}`,
    TOGGLE_POPULAR: (id: string) => `/packages/${id}/toggle-popular`,
  },
  ORDERS: {
    LIST: '/orders',
    MY_ORDERS: '/orders/my-orders',
    DETAIL: (id: string) => `/orders/${id}`,
    CREATE: '/orders',
    UPDATE_STATUS: (id: string) => `/orders/${id}/status`,
    REFUND: (id: string) => `/orders/${id}/refund`,
    STATS: '/orders/stats/summary',
  },
  PAYMENTS: {
    LIST: '/payments',
    MY_PAYMENTS: '/payments/my-payments',
    INITIALIZE: '/payments/initialize',
    VERIFY: '/payments/verify',
    MOBILE_MONEY: '/payments/mobile-money',
    APPROVE: (id: string) => `/payments/${id}/approve`,
    REJECT: (id: string) => `/payments/${id}/reject`,
    STATS: '/payments/stats/summary',
  },
  WALLETS: {
    BALANCE: '/wallets/balance',
    TRANSACTIONS: '/wallets/transactions',
    TOPUP: '/wallets/topup',
    COMPLETE_TOPUP: '/wallets/topup/complete',
    STATS: '/wallets/stats',
    ADMIN_TRANSACTIONS: '/wallets/admin/transactions',
    ADJUST: '/wallets/admin/adjust',
    USER_WALLET: (id: string) => `/wallets/${id}`,
  },
  USERS: {
    LIST: '/users',
    DETAIL: (id: string) => `/users/${id}`,
    UPDATE: (id: string) => `/users/${id}`,
    DELETE: (id: string) => `/users/${id}`,
    TOGGLE_STATUS: (id: string) => `/users/${id}/toggle-status`,
    VERIFY: (id: string) => `/users/${id}/verify`,
    STATS: '/users/stats/summary',
    USER_WALLET: (id: string) => `/users/${id}/wallet`,
  },
  ADMIN: {
    DASHBOARD_STATS: '/admin/dashboard/stats',
    SYSTEM_HEALTH: '/admin/system/health',
    ANALYTICS: '/admin/analytics',
    ACTIVITY: '/admin/activity',
    EXPORT: (type: string) => `/admin/export/${type}`,
  },
  BULK_ORDERS: {
    LIST: '/bulk-orders',
    CREATE: '/bulk-orders',
    DETAIL: (id: string) => `/bulk-orders/${id}`,
    PAY: (id: string) => `/bulk-orders/${id}/pay`,
    CANCEL: (id: string) => `/bulk-orders/${id}/cancel`,
    STATS: '/bulk-orders/stats/overview',
  },
} as const;

// Status configurations
export const ORDER_STATUS = {
  PENDING: 'pending',
  PENDING_PAYMENT: 'pending_payment',
  PAID: 'paid',
  ACCEPTED: 'accepted',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  REFUNDED: 'refunded',
} as const;

export const BULK_ORDER_STATUS = {
  DRAFT: 'draft',
  PENDING_PAYMENT: 'pending_payment',
  PENDING_ADMIN_PROCESSING: 'pending_admin_processing',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  PARTIALLY_COMPLETED: 'partially_completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
} as const;

export const PAYMENT_STATUS = {
  PENDING: 'pending',
  VERIFIED: 'verified',
  FAILED: 'failed',
  REFUNDED: 'refunded',
} as const;

export const TRANSACTION_STATUS = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

// Payment methods
export const PAYMENT_METHODS = {
  PAYSTACK: 'paystack',
  MOBILE_MONEY: 'mobile_money',
  WALLET: 'wallet',
} as const;

// User roles
export const USER_ROLES = {
  USER: 'user',
  AGENT: 'agent',
  ADMIN: 'admin',
} as const;

// Transaction categories
export const TRANSACTION_CATEGORIES = {
  TOPUP: 'topup',
  PURCHASE: 'purchase',
  REFUND: 'refund',
  BONUS: 'bonus',
  PENALTY: 'penalty',
  WITHDRAWAL: 'withdrawal',
} as const;

// Package categories
export const PACKAGE_CATEGORIES = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  SOCIAL: 'social',
  BUSINESS: 'business',
  GAMING: 'gaming',
  STREAMING: 'streaming',
} as const;

// Pagination defaults
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100,
} as const;

// Validation patterns
export const VALIDATION_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^0[2-9]\d{8}$/,
  PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/,
  USERNAME: /^[a-zA-Z0-9_]{3,30}$/,
  GHANA_PHONE: /^0(20|23|24|25|26|27|28|29|30|31|32|33|34|35|36|37|38|39|40|41|42|43|44|45|46|47|48|49|50|51|52|53|54|55|56|57|58|59)\d{7}$/,
} as const;

// Error messages
export const ERROR_MESSAGES = {
  INVALID_EMAIL: 'Please enter a valid email address',
  INVALID_PHONE: 'Please enter a valid Ghana phone number',
  INVALID_PASSWORD: 'Password must be at least 8 characters with uppercase, lowercase, and number',
  INVALID_USERNAME: 'Username must be 3-30 characters with letters, numbers, and underscores',
  REQUIRED_FIELD: 'This field is required',
  NETWORK_ERROR: 'Network error. Please check your connection.',
  SERVER_ERROR: 'Server error. Please try again later.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  FORBIDDEN: 'You do not have permission to access this resource.',
  NOT_FOUND: 'The requested resource was not found.',
} as const;

// Success messages
export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: 'Login successful!',
  REGISTER_SUCCESS: 'Registration successful!',
  PROFILE_UPDATED: 'Profile updated successfully!',
  PASSWORD_CHANGED: 'Password changed successfully!',
  ORDER_CREATED: 'Order created successfully!',
  PAYMENT_COMPLETED: 'Payment completed successfully!',
  WALLET_TOPUP: 'Wallet topped up successfully!',
  PACKAGE_UPDATED: 'Package updated successfully!',
  USER_UPDATED: 'User updated successfully!',
} as const;

// Currency and formatting
export const CURRENCY = {
  CODE: 'GHS',
  SYMBOL: 'GHS',
  DECIMAL_PLACES: 2,
} as const;

// Re-export from currency utility for convenience
export { 
  formatCurrency, 
  formatAmount, 
  formatTransactionAmount,
  parseCurrency 
} from './currency';

// Date formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM dd, yyyy',
  DISPLAY_TIME: 'MMM dd, yyyy HH:mm',
  SHORT: 'dd/MM/yyyy',
  ISO: 'yyyy-MM-dd',
  TIME: 'HH:mm:ss',
} as const;

// Local storage keys
export const STORAGE_KEYS = {
  TOKEN: 'token',
  THEME: 'theme',
  LANGUAGE: 'language',
  USER_PREFERENCES: 'userPreferences',
  NOTIFICATIONS: 'notifications',
  CART: 'cart',
} as const;

export const COMMUNITY_LINKS = {
  TELEGRAM: 'https://t.me/ghdata_market1',
  WHATSAPP: 'https://chat.whatsapp.com/IxhspoEV1N2IlC4I1ZCQv0',
  /** Direct 1-on-1 chat with the admin (Ghana +233, no leading zero) */
  WHATSAPP_DIRECT: 'https://wa.me/233530603959?text=Hello%2C%20I%20need%20support%20with%20my%20account.',
} as const;

export const SUPPORT_CONTACT = {
  PHONE: '0530603959',
  EMAIL: 'devefo635@gmail.com',
} as const;

// Animation durations
export const ANIMATION_DURATIONS = {
  FAST: 150,
  NORMAL: 300,
  SLOW: 500,
} as const;

// Breakpoints
export const BREAKPOINTS = {
  SM: 640,
  MD: 768,
  LG: 1024,
  XL: 1280,
  '2XL': 1536,
} as const;

// Socket events
export const SOCKET_EVENTS = {
  CONNECT: 'connect',
  DISCONNECT: 'disconnect',
  JOIN_ORDER: 'joinOrder',
  LEAVE_ORDER: 'leaveOrder',
  SUBSCRIBE_PACKAGES: 'subscribePackages',
  UNSUBSCRIBE_PACKAGES: 'unsubscribePackages',
  SUBSCRIBE_WALLET: 'subscribeWallet',
  SUBSCRIBE_ADMIN_DASHBOARD: 'subscribeAdminDashboard',
  PACKAGE_UPDATE: 'packageUpdate',
  ORDER_UPDATE: 'orderUpdate',
  WALLET_UPDATE: 'walletUpdate',
  PAYMENT_UPDATE: 'paymentUpdate',
  DASHBOARD_UPDATE: 'dashboardUpdate',
  SYSTEM_NOTIFICATION: 'systemNotification',
  NOTIFICATION: 'notification',
  ORDER_TRACKING: 'orderTracking',
  BULK_ORDER_UPDATE: 'bulkOrderUpdate',
} as const;
